import { useSyncExternalStore } from 'react';
import { isOutageError, observeTransport } from './api.js';

export { isOutageError };

/**
 * Sistemin ulaşılabilirliği, planlı bakım ve yeni sürüm — bakım ekranının TEK durum kaynağı
 * (tasarım: docs/superpowers/specs/2026-09-22-bakim-ekrani-design.md).
 *
 * Üç girdisi vardır:
 *  - API katmanının kesinti sinyalleri (`reportOutageSignal`, data/api.ts `send`);
 *  - ön kapının durum dosyası `/_bakim/durum.json` (planlı bakım ve duyurusu);
 *  - yayının sürüm dosyası `/version.json` (kesintisiz güncellemeden sonra "yeni sürüm hazır").
 *
 * Bu istekler `send`den GEÇMEZ: kesinti sırasında kendi sinyalini üretip kendini besleyen bir
 * döngü kurardı. Ayrıca `/api/v1` öneki taşımazlar ve oturumdan bağımsızdırlar.
 */

export type BakimModu = 'duyuru' | 'planli';

/** Ön kapının durum dosyası (sözleşme: tasarım belgesi, "Sözleşme" tablosu). */
export interface BakimDurumu {
  mod: BakimModu;
  /** ISO-8601; duyuruda bakımın başlayacağı, planlıda başladığı an. */
  baslangic: string;
  tahminiBitis: string | null;
  not: string | null;
}

export interface SystemStatus {
  /** Kesinti katmanı açık: uygulama cevap vermiyor. */
  outage: boolean;
  /** Durum dosyasındaki planlı bakım (duyuru ya da planlı); yoksa null. */
  planned: BakimDurumu | null;
  /** Planlı bakım fiilen başladı (dosya "planli" dedi ya da duyurunun süresi doldu). */
  plannedActive: boolean;
  /** Sunucu saati − istemci saati (ms). Geri sayım bununla düzeltilir. */
  clockOffsetMs: number;
  /** Kesintisiz güncellemeden sonra hazır olan sürüm; yoksa null. */
  update: { surum: string } | null;
}

/** Katmanı açan eşik: bu pencere içinde iki kesinti sinyali (tek yavaş istek herkesi atmaz). */
export const OUTAGE_WINDOW_MS = 10_000;
/** Katman açıkken yoklama aralığı; ±2 sn sapma bütün sekmelerin aynı anda vurmasını önler. */
export const RECOVERY_POLL_MS = 10_000;
const RECOVERY_JITTER_MS = 2_000;
export const MAINTENANCE_POLL_MS = 30_000;
export const VERSION_POLL_MS = 60_000;
/**
 * Duyurunun başlangıcı geçtiği hâlde dosya hâlâ "duyuru" diyorsa sunucunun zamanlayıcısı birkaç
 * saniye geç kalmıştır: bu süre boyunca bakım BAŞLIYOR sayılır (katman kapanıp açılmaz). Süreyi
 * aşan dosya bayattır (zamanlayıcı hiç çalışmadı): yok sayılır, kimse bakımda takılı kalmaz.
 */
export const LATE_START_GRACE_MS = 120_000;

export const DURUM_URL = '/_bakim/durum.json';
export const VERSION_URL = '/version.json';
export const READY_URL = '/api/readyz';

const INITIAL: SystemStatus = {
  outage: false,
  planned: null,
  plannedActive: false,
  clockOffsetMs: 0,
  update: null,
};

let state: SystemStatus = INITIAL;
const listeners = new Set<() => void>();
const recoveredListeners = new Set<() => void>();
let signals: number[] = [];
let probing = false;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let countdownTimer: ReturnType<typeof setTimeout> | null = null;
let backgroundTimers: ReturnType<typeof setInterval>[] = [];
let detachFocus: (() => void) | null = null;

function set(patch: Partial<SystemStatus>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

// API katmanı her isteğin sonucunu buraya bildirir (döngüsüz kayıt, data/api.ts).
observeTransport((outcome) => {
  if (outcome === 'outage') reportOutageSignal();
  else reportServerReachable();
});

export function getSystemStatus(): SystemStatus {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSystemStatus(): SystemStatus {
  return useSyncExternalStore(subscribe, getSystemStatus, getSystemStatus);
}

/** Kesinti katmanı kapanınca (sistem döndü) çağrılır; kabuk bununla sorguları tazeler. */
export function onSystemRecovered(listener: () => void): () => void {
  recoveredListeners.add(listener);
  return () => recoveredListeners.delete(listener);
}

async function fetchNoStore(url: string): Promise<Response | null> {
  try {
    return await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
  } catch {
    return null;
  }
}

async function isReady(): Promise<boolean> {
  const res = await fetchNoStore(READY_URL);
  return res !== null && res.ok;
}

/** API katmanı: kesinti sayılan bir hata geldi. */
export function reportOutageSignal(): void {
  if (state.outage) return;
  const now = Date.now();
  signals = signals.filter((t) => now - t < OUTAGE_WINDOW_MS);
  signals.push(now);
  if (signals.length >= 2) {
    openOutage();
    return;
  }
  if (probing) return;
  probing = true;
  void isReady().then((ok) => {
    probing = false;
    if (ok) signals = [];
    else openOutage();
  });
}

/** API katmanı: sunucu cevap verdi (başarı ya da anlamlı bir 4xx/5xx). Sayaç sıfırlanır. */
export function reportServerReachable(): void {
  signals = [];
}

/**
 * Eşiği beklemeden katmanı açar. Açılışta `/auth/me` kesintiyle düşerse (giriş ekranı yerine
 * bakım) ve eksik parça yenilemesi döngüye girerse kullanılır.
 */
export function reportOutageNow(): void {
  openOutage();
}

function openOutage(): void {
  if (!state.outage) set({ outage: true });
  scheduleRecovery();
}

function scheduleRecovery(): void {
  if (recoveryTimer !== null) return;
  const delay = RECOVERY_POLL_MS + (Math.random() * 2 - 1) * RECOVERY_JITTER_MS;
  recoveryTimer = setTimeout(() => {
    recoveryTimer = null;
    void recoveryTick();
  }, delay);
}

async function recoveryTick(): Promise<void> {
  if (!state.outage && !state.plannedActive) return;
  await pollMaintenance();
  // Planlı bakım sürerken (ya da başlamak üzereyken) API'ye hiç sorulmaz: ön kapı 503 döner.
  if (state.planned?.mod === 'planli' || startingNow(state.planned)) {
    scheduleRecovery();
    return;
  }
  if (await isReady()) {
    recover();
    return;
  }
  scheduleRecovery();
}

function recover(): void {
  signals = [];
  set({ outage: false, plannedActive: false });
  for (const listener of recoveredListeners) listener();
  void checkVersion();
}

function parseDurum(body: unknown): BakimDurumu | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  if (b.v !== 1 || (b.mod !== 'duyuru' && b.mod !== 'planli')) return null;
  if (typeof b.baslangic !== 'string' || Number.isNaN(Date.parse(b.baslangic))) return null;
  const tahminiBitis =
    typeof b.tahminiBitis === 'string' && !Number.isNaN(Date.parse(b.tahminiBitis))
      ? b.tahminiBitis
      : null;
  const not = typeof b.not === 'string' && b.not.trim() !== '' ? b.not : null;
  return { mod: b.mod, baslangic: b.baslangic, tahminiBitis, not };
}

/** Sunucunun saatine göre "şimdi" (ms). */
export function serverNow(): number {
  return Date.now() + state.clockOffsetMs;
}

/** Duyurunun başlangıcı geçti ve henüz bayat değil: bakım şu anda başlıyor. */
function startingNow(planned: BakimDurumu | null): boolean {
  if (planned?.mod !== 'duyuru') return false;
  const since = serverNow() - Date.parse(planned.baslangic);
  return since >= 0 && since < LATE_START_GRACE_MS;
}

/** Durum dosyasını okur; duyuruysa başlangıç anına geri sayımı kurar. */
export async function pollMaintenance(): Promise<void> {
  const res = await fetchNoStore(DURUM_URL);
  if (res === null) return; // Ön kapıya da ulaşılamıyor: bilinen durumu koru.
  const header = res.headers.get('date');
  const serverDate = header ? Date.parse(header) : Number.NaN;
  const clockOffsetMs = Number.isNaN(serverDate) ? state.clockOffsetMs : serverDate - Date.now();
  let planned: BakimDurumu | null = null;
  if (res.ok) {
    try {
      planned = parseDurum(await res.json());
    } catch {
      planned = null;
    }
  }
  const wasActive = state.plannedActive;
  set({ planned, clockOffsetMs });
  const plannedActive = planned?.mod === 'planli' || startingNow(planned);
  set({ plannedActive });
  armCountdown();
  if (plannedActive) scheduleRecovery();
  // Bakım, katmanın kendi yoklaması yerine arka plan/odak yoklamasında bittiyse de bu bir
  // DÖNÜŞTÜR: sorgular tazelenir ve sürüm sorulur (yoksa boşta bekleyen ekran bayat kalırdı).
  else if (wasActive && !state.outage) recover();
}

function armCountdown(): void {
  if (countdownTimer !== null) clearTimeout(countdownTimer);
  countdownTimer = null;
  const planned = state.planned;
  if (planned?.mod !== 'duyuru' || state.plannedActive) return;
  const wait = Date.parse(planned.baslangic) - serverNow();
  if (wait <= -LATE_START_GRACE_MS) return; // Bayat duyuru: zamanlayıcı hiç çalışmadı.
  const begin = () => {
    countdownTimer = null;
    set({ plannedActive: true });
    scheduleRecovery();
  };
  if (wait <= 0) begin();
  else countdownTimer = setTimeout(begin, wait);
}

function ownEntryScript(): string | null {
  const script = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/entry-"]',
  );
  const src = script?.getAttribute('src');
  return src ? (src.split('/').pop() ?? null) : null;
}

/**
 * Yayının sürüm dosyasını kendi giriş betiğimizle karşılaştırır. Geliştirme sunucusunda dosya
 * yoktur (404) ve giriş betiği farklı adlandırılır: ikisi de "güncelleme yok" sayılır.
 */
export async function checkVersion(): Promise<void> {
  if (state.update) return;
  const res = await fetchNoStore(VERSION_URL);
  if (res === null || !res.ok) return;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return;
  }
  const b = body as { surum?: unknown; giris?: unknown };
  if (typeof b.surum !== 'string' || typeof b.giris !== 'string') return;
  const own = ownEntryScript();
  if (own !== null && own !== b.giris) set({ update: { surum: b.surum } });
}

/**
 * Arka plan yoklamalarını başlatır: durum dosyası 30 sn'de, sürüm dosyası 60 sn'de bir ve
 * pencereye her dönüldüğünde. Uygulama kökü bir kez çağırır.
 */
export function startSystemStatus(): () => void {
  stopBackground();
  void pollMaintenance();
  void checkVersion();
  backgroundTimers = [
    setInterval(() => void pollMaintenance(), MAINTENANCE_POLL_MS),
    setInterval(() => void checkVersion(), VERSION_POLL_MS),
  ];
  const onFocus = () => {
    if (document.visibilityState === 'hidden') return;
    void pollMaintenance();
    void checkVersion();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);
  detachFocus = () => {
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
  return stopSystemStatus;
}

function stopBackground(): void {
  for (const timer of backgroundTimers) clearInterval(timer);
  backgroundTimers = [];
  detachFocus?.();
  detachFocus = null;
}

export function stopSystemStatus(): void {
  stopBackground();
  if (recoveryTimer !== null) clearTimeout(recoveryTimer);
  if (countdownTimer !== null) clearTimeout(countdownTimer);
  recoveryTimer = null;
  countdownTimer = null;
}

export function resetSystemStatusForTests(): void {
  stopSystemStatus();
  state = INITIAL;
  signals = [];
  probing = false;
  listeners.clear();
  recoveredListeners.clear();
}
